/**
 * Shows a form with the CSS class `form-steps` one step at a time.
 *
 * Every field is a step of its own, except that a Section field starts a step
 * the fields after it share, up to the next section or a field with the CSS
 * class `step-own`, which goes back to a step of its own. A step whose fields are
 * all hidden by conditional logic (form-logic.js) is skipped and left out of
 * the count, so the last step is whichever one is last for these answers.
 *
 * A step that starts with a field classed `step-sub` is a part of the step
 * before it: it is numbered after it, 7.1 after 7, and not counted on its own.
 *
 * Next and Submit look shut (aria-disabled) until the step is answered, but
 * still take a click, which marks what is missing.
 *
 * It is the same single-page form underneath: Next only checks that the step
 * is answered, and everything is submitted and validated together on the
 * server. If the server sends the form back with errors, it opens on the first
 * step that has one. Without JavaScript the whole form shows and still works.
 */
( function() {
	const strings = Object.assign(
		{
			step: 'Step %1$s of %2$s',
			next: 'Next',
			back: 'Back',
			required: 'This field is required.',
			email: 'Please enter a valid email address.',
		},
		window.wgmFormSteps || {}
	);

	const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

	document.querySelectorAll( 'form.form-steps' ).forEach( setup );

	/**
	 * Turn one form into steps.
	 *
	 * @param {HTMLFormElement} form The form.
	 */
	function setup( form ) {
		const list = form.querySelector( '.gform_fields' );
		const footer = form.querySelector( '.gform_footer' );
		const submit = footer && footer.querySelector( '[type="submit"]' );

		if ( ! list || ! submit ) {
			return;
		}

		const steps = group( list );

		if ( steps.length < 2 ) {
			return;
		}

		const progress = buildProgress();
		const back = progress.back;
		const next = buildButton( strings.next, 'form-steps__next' );

		form.querySelector( '.gform_body' ).before( progress.root );
		submit.before( next );
		form.classList.add( 'is-stepped' );

		// Open on the first step the server flagged, if any.
		let current = Math.max( 0, steps.findIndex( function( step ) {
			return step.some( function( field ) {
				return field.classList.contains( 'gfield_error' );
			} );
		} ) );

		function available() {
			return steps.filter( function( step ) {
				return step.some( function( field ) {
					return ! field.hidden;
				} );
			} );
		}

		function render( focus ) {
			const active = available();
			const step = steps[ current ];
			const position = active.indexOf( step );
			const last = position === active.length - 1;

			steps.forEach( function( fields ) {
				fields.forEach( function( field ) {
					field.classList.toggle( 'is-step-inactive', fields !== step );
				} );
			} );

			progress.backWrap.hidden = position <= 0;
			next.hidden = last;
			submit.hidden = ! last;
			settle();

			progress.count.textContent = strings.step
				.replace( '%1$s', stepNumber( active, position ) )
				.replace( '%2$s', String( active.filter( isMainStep ).length ) );
			progress.bar.style.width = ( ( position + 1 ) / active.length * 100 ) + '%';

			if ( focus ) {
				enter( step );
			}
		}

		// The way forward looks shut until the step is answered.
		function settle() {
			const ready = answered( steps[ current ] );

			setDisabled( next, ! ready );
			setDisabled( submit, next.hidden && ! ready );
		}

		function go( direction ) {
			const active = available();
			const target = active[ active.indexOf( steps[ current ] ) + direction ];

			if ( target ) {
				current = steps.indexOf( target );
				render( true );
			}
		}

		next.addEventListener( 'click', function() {
			if ( check( steps[ current ] ) ) {
				go( 1 );
			}
		} );

		back.addEventListener( 'click', function( event ) {
			event.preventDefault();
			go( -1 );
		} );

		// Enter in a text box would submit the whole form from the middle.
		form.addEventListener( 'submit', function( event ) {
			if ( ! next.hidden ) {
				event.preventDefault();
				next.click();
			} else if ( ! check( steps[ current ] ) ) {
				event.preventDefault();
			}
		} );

		// An answer can add or remove steps further on.
		form.addEventListener( 'wgm:logic', function() {
			render( false );
		} );

		form.addEventListener( 'change', function( event ) {
			const field = event.target.closest( '.gfield' );

			if ( field ) {
				clearError( field );
			}

			settle();
		} );

		form.addEventListener( 'input', settle );

		// Answers the browser puts back on reload or Back arrive without events.
		window.addEventListener( 'pageshow', settle );

		render( false );
	}

	/**
	 * Shut or open a button. Shut is only a look: a click still goes through,
	 * to be checked and told what is missing. One that opens plays its hover
	 * once, to show the way forward is open.
	 *
	 * @param {HTMLButtonElement} button   The button.
	 * @param {boolean}           disabled Whether to shut it.
	 */
	function setDisabled( button, disabled ) {
		const opens = 'true' === button.getAttribute( 'aria-disabled' ) && ! disabled && ! button.hidden;

		button.setAttribute( 'aria-disabled', String( disabled ) );

		if ( ! opens ) {
			return;
		}

		clearTimeout( button.wgmHoverTimer );
		button.classList.remove( 'is-hover' );
		// Restart the animation.
		void button.offsetWidth;
		button.classList.add( 'is-hover' );

		// As long as the shine takes (button.css).
		button.wgmHoverTimer = setTimeout( function() {
			button.classList.remove( 'is-hover' );
		}, 1200 );
	}

	/**
	 * Split the fields into steps.
	 *
	 * @param {HTMLElement} list The form's field list.
	 * @return {HTMLElement[][]} The fields of each step.
	 */
	function group( list ) {
		const steps = [];
		let section = false;

		Array.from( list.children ).forEach( function( field ) {
			if ( ! field.classList.contains( 'gfield' ) || isOffscreen( field ) ) {
				return;
			}

			if ( field.classList.contains( 'gfield--type-section' ) ) {
				steps.push( [ field ] );
				section = true;
			} else if ( field.classList.contains( 'step-own' ) || field.classList.contains( 'step-sub' ) ) {
				steps.push( [ field ] );
				section = false;
			} else if ( section ) {
				steps[ steps.length - 1 ].push( field );
			} else {
				steps.push( [ field ] );
			}
		} );

		return steps;
	}

	/**
	 * Whether a step is counted on its own, not a part of the one before it.
	 *
	 * @param {HTMLElement[]} step  The step's fields.
	 * @param {number}        index Its place among the steps shown.
	 * @return {boolean} True for a step of its own.
	 */
	function isMainStep( step, index ) {
		return 0 === index || ! step[ 0 ].classList.contains( 'step-sub' );
	}

	/**
	 * A step's number as shown: 7, or 7.1 for the first part after step 7.
	 *
	 * @param {HTMLElement[][]} active   The steps shown.
	 * @param {number}          position The step's place among them.
	 * @return {string} The number.
	 */
	function stepNumber( active, position ) {
		let main = 0;
		let sub = 0;

		active.slice( 0, position + 1 ).forEach( function( step, index ) {
			if ( isMainStep( step, index ) ) {
				main++;
				sub = 0;
			} else {
				sub++;
			}
		} );

		return sub ? main + '.' + sub : String( main );
	}

	/**
	 * Fields that never show: hidden inputs, the honeypot, admin-only fields.
	 *
	 * @param {HTMLElement} field A field.
	 * @return {boolean} True when the field is never shown.
	 */
	function isOffscreen( field ) {
		return [ 'gform_hidden', 'gfield--type-honeypot', 'gfield_visibility_hidden', 'gform_validation_container' ]
			.some( function( name ) {
				return field.classList.contains( name );
			} );
	}

	/**
	 * Check that a step's shown, required fields are answered, and mark those that are not.
	 *
	 * @param {HTMLElement[]} step The step's fields.
	 * @return {boolean} True when the step can be left.
	 */
	function check( step ) {
		let firstInvalid = null;

		step.forEach( function( field ) {
			if ( ! isShown( field ) ) {
				return;
			}

			const problem = fieldProblem( field );

			if ( problem ) {
				showError( field, problem );
				firstInvalid = firstInvalid || field;
			} else {
				clearError( field );
			}
		} );

		if ( firstInvalid ) {
			const control = firstInvalid.querySelector( 'input:not([type="hidden"]), select, textarea' );

			if ( control ) {
				control.focus();
			}

			return false;
		}

		return true;
	}

	/**
	 * Whether a step's shown fields are all answered, without marking any.
	 *
	 * @param {HTMLElement[]} step The step's fields.
	 * @return {boolean} True when the step can be left.
	 */
	function answered( step ) {
		return step.every( function( field ) {
			return ! isShown( field ) || ! fieldProblem( field );
		} );
	}

	/**
	 * Whether a field is in view: not hidden by conditional logic, nor in a
	 * repeated row not yet opened (form-repeat.js).
	 *
	 * @param {HTMLElement} field A field.
	 * @return {boolean} True when it shows.
	 */
	function isShown( field ) {
		return ! field.hidden && ! field.classList.contains( 'is-repeat-collapsed' );
	}

	/**
	 * What is wrong with a field, if anything.
	 *
	 * @param {HTMLElement} field A field.
	 * @return {string} The message to show, or an empty string.
	 */
	function fieldProblem( field ) {
		const controls = Array.from( field.querySelectorAll( 'input:not([type="hidden"]), select, textarea' ) );
		const required = field.classList.contains( 'gfield_contains_required' ) || rowStarted( field );
		const own = field.dataset.wgmError;

		if ( required ) {
			const choices = controls.filter( function( control ) {
				return [ 'radio', 'checkbox' ].includes( control.type );
			} );

			const answered = choices.length
				? choices.some( function( control ) {
					return control.checked;
				} )
				: controls.every( function( control ) {
					return control.disabled || '' !== control.value.trim();
				} );

			if ( ! answered ) {
				return own || strings.required;
			}
		}

		const badEmail = controls.some( function( control ) {
			return 'email' === control.type && '' !== control.value && ! control.checkValidity();
		} );

		return badEmail ? own || strings.email : '';
	}

	/**
	 * Whether a `repeat-row-needed` field's row has anything typed in it, which
	 * makes the field required (form-repeat.js; the server checks the same).
	 *
	 * @param {HTMLElement} field A field.
	 * @return {boolean} True when the field must be filled.
	 */
	function rowStarted( field ) {
		const row = Array.from( field.classList ).find( function( name ) {
			return /^repeat-row-\d+$/.test( name );
		} );

		if ( ! row || ! field.classList.contains( 'repeat-row-needed' ) ) {
			return false;
		}

		return Array.from( field.parentElement.querySelectorAll( '.' + row + ' :is(input:not([type="hidden"]), textarea)' ) )
			.some( function( control ) {
				return '' !== control.value.trim();
			} );
	}

	/**
	 * Mark a field as wrong, the way the server does.
	 *
	 * @param {HTMLElement} field   A field.
	 * @param {string}      message What to say.
	 */
	function showError( field, message ) {
		let note = field.querySelector( '.form-steps__error' );

		if ( ! note ) {
			note = document.createElement( 'div' );
			note.className = 'gfield_description validation_message gfield_validation_message form-steps__error';
			note.id = field.id + '_step_error';
			field.append( note );
		}

		note.textContent = message;
		field.classList.add( 'gfield_error' );

		field.querySelectorAll( 'input, select, textarea' ).forEach( function( control ) {
			control.setAttribute( 'aria-invalid', 'true' );
		} );
	}

	/**
	 * Clear a field's mark, server-sent or ours.
	 *
	 * @param {HTMLElement} field A field.
	 */
	function clearError( field ) {
		if ( ! field.classList.contains( 'gfield_error' ) ) {
			return;
		}

		field.classList.remove( 'gfield_error' );
		field.querySelectorAll( '.validation_message' ).forEach( function( note ) {
			note.remove();
		} );
		field.querySelectorAll( '[aria-invalid="true"]' ).forEach( function( control ) {
			control.setAttribute( 'aria-invalid', 'false' );
		} );

		// The server's summary above the form goes once nothing it lists is left.
		const wrapper = field.closest( '.gform_wrapper' );

		if ( wrapper && ! wrapper.querySelector( '.gfield_error' ) ) {
			wrapper.querySelectorAll( '.gform_validation_errors' ).forEach( function( summary ) {
				summary.remove();
			} );
		}
	}

	/**
	 * Bring a step in: replay its entrance, move focus to it, and scroll the
	 * form back into view if the step's top is out of sight.
	 *
	 * @param {HTMLElement[]} step The step's fields.
	 */
	function enter( step ) {
		const first = step.find( function( field ) {
			return ! field.hidden;
		} );

		if ( ! first ) {
			return;
		}

		step.forEach( function( field ) {
			field.classList.remove( 'is-step-entering' );
			// Restart the animation.
			void field.offsetWidth;
			field.classList.add( 'is-step-entering' );
		} );

		const heading = first.querySelector( 'legend, .gsection_title, label' ) || first;

		if ( ! heading.hasAttribute( 'tabindex' ) ) {
			heading.setAttribute( 'tabindex', '-1' );
		}

		heading.focus( { preventScroll: true } );

		const form = first.closest( 'form' );

		if ( form && form.getBoundingClientRect().top < 0 ) {
			form.scrollIntoView( { behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' } );
		}
	}

	/**
	 * The progress bar, in Gravity Forms' own markup so it is styled the same,
	 * with the way back after the count: "Step 2 of 7 — Back".
	 *
	 * @return {{root: HTMLElement, count: HTMLElement, backWrap: HTMLElement, back: HTMLAnchorElement, bar: HTMLElement}} Its parts.
	 */
	function buildProgress() {
		const root = document.createElement( 'div' );
		root.className = 'gf_progressbar_wrapper form-steps__progress';
		root.innerHTML = '<p class="gf_progressbar_title">'
			+ '<span class="form-steps__count" aria-live="polite"></span>'
			+ '<span class="form-steps__back-wrap"> &mdash; <a class="form-steps__back" href="#"></a></span>'
			+ '</p>'
			+ '<div class="gf_progressbar" aria-hidden="true"><div class="gf_progressbar_percentage"></div></div>';

		const back = root.querySelector( '.form-steps__back' );
		back.textContent = strings.back;

		return {
			root: root,
			count: root.querySelector( '.form-steps__count' ),
			backWrap: root.querySelector( '.form-steps__back-wrap' ),
			back: back,
			bar: root.querySelector( '.gf_progressbar_percentage' ),
		};
	}

	/**
	 * A footer button, with the submit button's shimmer.
	 *
	 * @param {string} label     Its text.
	 * @param {string} className Its own class.
	 * @return {HTMLButtonElement} The button.
	 */
	function buildButton( label, className ) {
		const button = document.createElement( 'button' );
		button.type = 'button';
		button.className = 'button shimmer ' + className;
		button.innerHTML = '<span class="text"></span><span class="effect-shimmer" aria-hidden="true"></span>';
		button.querySelector( '.text' ).textContent = label;

		return button;
	}
}() );
